import { useState, useCallback, useMemo } from 'react'
import { useLocation, useParams, useNavigate } from 'react-router-dom'
import { MAIN_NAV, type NavItem } from '../config/navigation'
import { useProjectAccess } from './useProjectAccess'

export function useShellNavigation() {
  const { projectId } = useParams()
  const location = useLocation()
  const navigate = useNavigate()

  const { hasModule, isSuperAdmin, modules } = useProjectAccess(
    projectId ? Number(projectId) : undefined
  )

  // Filtre les items top-level selon l'acces du user au projet courant.
  // - super_admin ou hors contexte projet : nav complete (pas de filtrage)
  // - sinon : item visible si hasModule(item.id) ; 'overview' visible des
  //   qu'il existe au moins un module (page d'atterrissage).
  const visibleMainNav = useMemo((): NavItem[] => {
    if (isSuperAdmin || !projectId) return MAIN_NAV
    return MAIN_NAV.filter(item =>
      item.id === 'overview' ? modules.length > 0 : hasModule(item.id)
    )
  }, [isSuperAdmin, projectId, modules, hasModule])

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('orion-section-sidebar') === 'collapsed'
  })
  const [hoveredRailItem, setHoveredRailItem] = useState<string | null>(null)

  // Determine la section active a partir de l'URL
  const activeSection = useMemo((): NavItem | null => {
    if (!projectId) return null
    const prefix = `/p/${projectId}/`
    const path = location.pathname
    if (!path.startsWith(prefix)) return null
    const subPath = path.slice(prefix.length)

    // Cas special : path vide = overview
    if (!subPath || subPath === '') {
      return MAIN_NAV.find(item => item.id === 'overview') ?? null
    }

    // Cherche la section dont le path ou un des children/groups matche
    for (const item of MAIN_NAV) {
      // Match direct sur le path de l'item
      if (item.path && subPath.startsWith(item.path)) {
        // Verifier que ce n'est pas un faux positif (ex: 'a' match 'audit')
        const afterPath = subPath.slice(item.path.length)
        if (afterPath === '' || afterPath.startsWith('/')) {
          return item
        }
      }
      // Match sur les groups
      if (item.groups) {
        for (const group of item.groups) {
          for (const child of group.items) {
            if (subPath.startsWith(child.path)) {
              const afterPath = subPath.slice(child.path.length)
              if (afterPath === '' || afterPath.startsWith('/')) {
                return item
              }
            }
          }
        }
      }
      // Fallback sur children (structure actuelle)
      if (item.children) {
        for (const child of item.children) {
          if (subPath.startsWith(child.path)) {
            const afterPath = subPath.slice(child.path.length)
            if (afterPath === '' || afterPath.startsWith('/')) {
              return item
            }
          }
        }
      }
    }
    return null
  }, [location.pathname, projectId])

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev
      localStorage.setItem('orion-section-sidebar', next ? 'collapsed' : 'expanded')
      return next
    })
  }, [])

  const navigateToSection = useCallback(
    (item: NavItem) => {
      if (!projectId || item.disabled || item.comingSoon) return
      const path = item.path ?? ''
      navigate(`/p/${projectId}/${path}`)
    },
    [projectId, navigate]
  )

  return {
    activeSection,
    visibleMainNav,
    sidebarCollapsed,
    toggleSidebar,
    hoveredRailItem,
    setHoveredRailItem,
    navigateToSection,
    projectId: projectId ?? '',
  }
}
